/**
 * Pure helpers behind the Journal quick-entry form.
 *
 * The form state is a mood/energy 1-5 rating plus symptom + tag chip lists and a
 * free-text note. These helpers toggle chips, parse comma/enter chip input, test
 * whether the entry has any content, and build the `POST /api/journal` payload.
 * DOM-free so the state logic is unit-tested directly.
 */
import type { JournalBody, StoredJournalEntry } from '../api.js';

export interface JournalFormState {
  mood: number | null;
  energy: number | null;
  symptoms: string[];
  tags: string[];
  text: string;
}

export function emptyJournalForm(): JournalFormState {
  return { mood: null, energy: null, symptoms: [], tags: [], text: '' };
}

/** Toggle a 1-5 rating: selecting the current value clears it. */
export function toggleRating(current: number | null, value: number): number | null {
  return current === value ? null : value;
}

/** Add `value` if absent (deduped, trimmed), remove it if present. Immutable. */
export function toggleChip(list: string[], value: string): string[] {
  const v = value.trim();
  if (v.length === 0) return list;
  return list.includes(v) ? list.filter((x) => x !== v) : [...list, v];
}

/** Split raw chip input on commas into distinct trimmed tokens. */
export function parseChips(raw: string): string[] {
  const out: string[] = [];
  for (const piece of raw.split(',')) {
    const v = piece.trim();
    if (v.length > 0 && !out.includes(v)) out.push(v);
  }
  return out;
}

/** Add every token in `raw` to `list` (deduped, order-preserving). Immutable. */
export function addChips(list: string[], raw: string): string[] {
  const set = new Set(list);
  for (const token of parseChips(raw)) set.add(token);
  return [...set];
}

/** True when at least one field carries content (mirrors the server's rule). */
export function hasContent(s: JournalFormState): boolean {
  return (
    s.mood !== null ||
    s.energy !== null ||
    s.symptoms.length > 0 ||
    s.tags.length > 0 ||
    s.text.trim().length > 0
  );
}

/** Build the `POST /api/journal` body, omitting empty fields. */
export function buildJournalBody(s: JournalFormState): JournalBody {
  const body: JournalBody = {};
  if (s.mood !== null) body.mood = s.mood;
  if (s.energy !== null) body.energy = s.energy;
  if (s.symptoms.length > 0) body.symptoms = s.symptoms;
  if (s.tags.length > 0) body.tags = s.tags;
  const text = s.text.trim();
  if (text.length > 0) body.text = text;
  return body;
}

/** Group entries by their entryDate (YYYY-MM-DD), preserving input order. */
export function groupByDay(entries: StoredJournalEntry[]): { date: string; entries: StoredJournalEntry[] }[] {
  const groups: { date: string; entries: StoredJournalEntry[] }[] = [];
  const index = new Map<string, StoredJournalEntry[]>();
  for (const e of entries) {
    let bucket = index.get(e.entryDate);
    if (!bucket) {
      bucket = [];
      index.set(e.entryDate, bucket);
      groups.push({ date: e.entryDate, entries: bucket });
    }
    bucket.push(e);
  }
  return groups;
}
